import {
  Box,
  Button,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import variables from "../../../assets/styles/variables.module.scss";
import { AgentTheme } from "../../../Shared-ui/Types/Agent";

interface Props {
  chatIcon: string | null;
  setChatIcon: React.Dispatch<React.SetStateAction<string | null>>;
  profileImage: string | null;
  setProfileImage: React.Dispatch<React.SetStateAction<string | null>>;
  updateBubbleColor: any;
  updateIncomingTextColor: any;
  updateOutgoingTextColor: any;
  updateTitleColor: any;
  updateChatWindowBg: (color: string, mode: string, angle: number) => void;
  agentTheme: AgentTheme;
  saveThemeChanges: () => void;
}

const ChatInterfaceSettings: React.FC<Props> = (props) => {
  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    setState: React.Dispatch<React.SetStateAction<string | null>>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      alert("Only JPG, PNG, and SVG formats are allowed.");
      e.target.value = '';
      return;
    }

    if (file.size > 1024 * 1024) {
      alert("File size must be less than 1MB.");
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setState(reader.result as string);
    reader.readAsDataURL(file);
  };

  const [mode, setMode] = useState<"single" | "gradient">("single");
  const [color1, setColor1] = useState("#ffffff");
  const [color2, setColor2] = useState("#000000");
  const [angle, setAngle] = useState(180);
  const [titleColor, setTitleColor] = useState("#000000");
  const [outgoingTextColor, setOutgoingTextColor] = useState("#000000");
  const [incomingTextColor, setIncomingTextColor] = useState("#000000");
  const [bubbleColor, setBubbleColor] = useState(variables.primaryColor);

  const handleColor1Change = (value: string) => {
    setColor1(value);
    updateBackground(mode, value, color2, angle);
  };

  const handleColor2Change = (value: string) => {
    setColor2(value);
    updateBackground(mode, color1, value, angle);
  };

  const handleAngleChange = (value: number) => {
    setAngle(value);
    updateBackground(mode, color1, color2, value);
  };

  const handleModeChange = (_: any, newMode: "single" | "gradient") => {
    if (newMode) {
      setMode(newMode);
      updateBackground(newMode, color1, color2, angle);
    }
    
  };

  function splitGradientValues(gradient: string): (string | number)[] {
    // Extract the content inside the parentheses
    const inner = gradient
      .replace(/^linear-gradient\(/, '')
      .replace(/\)$/, '')
      .trim();

    // Split by comma and trim parts
    const parts = inner.split(',').map(part => part.trim());

    // Extract numeric angle (remove 'deg' and convert to number)
    const angle = parts[0].endsWith('deg') ? parseInt(parts[0]) : parts[0];

    // Combine angle and color values
    return [angle, ...parts.slice(1)];
  }

  useEffect(() => {
    if (props.agentTheme.mode === "gradient") {
      const [angle, color1, color2] = splitGradientValues(props.agentTheme.backgroundColor);
      setAngle(typeof angle === "number" ? angle : parseInt(angle, 10));
      setColor1(String(color1));
      setColor2(String(color2));
      setMode("gradient");
    } else {
      setMode("single");
    }
  }, [props.agentTheme.mode])

  const getGradientPreview = () => {
    if (props.agentTheme.mode === "gradient") {
      // return `linear-gradient(${props.agentTheme.angle}deg, ${props.agentTheme.backgroundColorOne}, ${props.agentTheme.backgroundColorTwo})`;
    }
    if (mode === "single") {
      return props.agentTheme.backgroundColor
    }
  }
  const gradientPreview = `linear-gradient(${angle}deg, ${color1}, #ffffff)`;

  const updateBackground = (
    // newMode = mode,
    // c1 = color1,
    // c2 = color2,
    // a = angle
    newMode: string,
    c1: string,
    c2: string,
    a: number
  ) => {
    if (newMode === "single") {
      props.updateChatWindowBg(c1, newMode, a);
    } else {
      props.updateChatWindowBg(`linear-gradient(${a}deg, ${c1}, ${c2})`, newMode, a);
    }

  };





  return (
    <Box
      sx={{
        marginTop: "12px",
        height: "100%",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6">Chat Interface</Typography>
        <Button variant="contained" disableElevation onClick={() => props.saveThemeChanges()}>
          Save
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1
        }}
      >
        {/* <Typography variant="h6" sx={{ mb: 2 }}>
          Color Theme
        </Typography> */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid #E3E3E3",
            borderRadius: "8px",
            p: 2,
            background: variables.while,
          }}
        >
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Profile picture
            </Typography>
            <Typography variant="body2">
              Supports JPG, PNG, and SVG up to 1MB
            </Typography>
          </Box>
          <Box sx={{ display: "flex" }}>
            {props.profileImage ? (
              <Box
                component="img"
                src={props.profileImage}
                alt="Profile"
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "8px",
                  objectFit: "contain",
                  border: "1px solid #ccc",
                  cursor: "pointer",
                  p: "4px",
                }}
                onClick={() => props.setProfileImage(null)} // Reset on click
              />
            ) : (
              <IconButton
                component="label"
                sx={{
                  width: 50,
                  height: 50,
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <CloudUploadIcon />
                <input
                  hidden
                  accept="image/jpeg, image/png, image/svg+xml"
                  type="file"
                  onChange={(e) => handleImageUpload(e, props.setProfileImage)}
                />
              </IconButton>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid #E3E3E3",
            borderRadius: "8px",
            p: 2,
            background: variables.while,
          }}
        >
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Chat icon
            </Typography>
            <Typography variant="body2">
              Supports JPG, PNG, and SVG up to 1MB
            </Typography>
          </Box>
          <Box sx={{ display: "flex" }}>
            {props.chatIcon ? (
              <Box
                component="img"
                src={props.chatIcon}
                alt="Chat Icon"
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "8px",
                  objectFit: "contain",
                  border: "1px solid #ccc",
                  cursor: "pointer",
                  p: "4px",
                }}
                onClick={() => props.setChatIcon(null)}
              />
            ) : (
              <IconButton
                component="label"
                sx={{
                  width: 50,
                  height: 50,
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <CloudUploadIcon />
                <input
                  hidden
                  accept="image/jpeg, image/png, image/svg+xml"
                  type="file"
                  onChange={(e) => handleImageUpload(e, props.setChatIcon)}
                />
              </IconButton>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            border: "1px solid #E3E3E3",
            borderRadius: "8px",
            p: 2,
            background: variables.while,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Background Color
              </Typography>
              <Typography variant="body2">
                You can change the background color of the chat interface.
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              size="small"
            >
              <ToggleButton value="single">Single</ToggleButton>
              <ToggleButton value="gradient">Gradient</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            {mode === "single" ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    size="small"
                    value={props.agentTheme.backgroundColor}
                    onChange={(e) => handleColor1Change(e.target.value)}
                    inputProps={{ maxLength: 7 }}
                    sx={{
                      width: "150px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        height: "50px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "0 16px",
                        height: "100%",
                        boxSizing: "border-box",
                        borderRadius: "8px",
                      },
                    }}
                  />
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      position: "absolute",
                      right: "7px",
                      top: "3px",
                    }}
                  >
                    <input
                      type="color"
                      value={props.agentTheme.backgroundColor}
                      onChange={(e) => handleColor1Change(e.target.value)}
                      style={{
                        width: "100%",
                        height: "100%",
                        background:props.agentTheme.backgroundColor,
                        cursor: "pointer",
                        borderRadius: "8px",
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        outline: "none",
                        border: "none",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    size="small"
                    value={color1}
                    onChange={(e) => handleColor1Change(e.target.value)}
                    inputProps={{ maxLength: 7 }}
                    sx={{
                      width: "150px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        height: "50px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "0 16px",
                        height: "100%",
                        boxSizing: "border-box",
                        borderRadius: "8px",
                      },
                    }}
                  />
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      position: "absolute",
                      right: "7px",
                      top: "3px",
                    }}
                  >
                    <input
                      type="color"
                      value={color1}
                      onChange={(e) => handleColor1Change(e.target.value)}
                      style={{
                        width: "100%",
                        height: "100%",
                        background: "#fff",
                        cursor: "pointer",
                        borderRadius: "8px",
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        outline: "none",
                        border: "none",
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ position: "relative" }}>
                  <TextField
                    size="small"
                    value={color1}
                    onChange={(e) => handleColor2Change(e.target.value)}
                    inputProps={{ maxLength: 7 }}
                    sx={{
                      width: "150px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        height: "50px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "0 16px",
                        height: "100%",
                        boxSizing: "border-box",
                        borderRadius: "8px",
                      },
                    }}
                  />
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      position: "absolute",
                      right: "7px",
                      top: "3px",
                    }}
                  >
                    <input
                      type="color"
                      value={color2}
                      onChange={(e) => handleColor2Change(e.target.value)}
                      style={{
                        width: "100%",
                        height: "100%",
                        background: "#fff",
                        cursor: "pointer",
                        borderRadius: "8px",
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        outline: "none",
                        border: "none",
                      }}
                    />
                  </Box>
                </Box>

                <TextField
                  type="number"
                  size="small"
                  label="Angle"
                  value={angle}
                  onChange={(e) => handleAngleChange(Number(e.target.value))}
                  sx={{
                    width: "80px",
                    "& .MuiOutlinedInput-root": {
                      height: "50px",
                      borderRadius: "8px",
                    },
                    "& .MuiInputBase-input": {
                      height: "100%",
                      boxSizing: "border-box",
                    },
                  }}
                />
                <Box
                  sx={{
                    width: 120,
                    height: 50,
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                    background: props.agentTheme.backgroundColor || gradientPreview,
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid #E3E3E3",
            borderRadius: "8px",
            p: 2,
            background: variables.while,
          }}
        >
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Title Color
            </Typography>
            <Typography variant="body2">
              You can change the title color of chat window.
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "center", position: "relative" }}
          >
            <TextField
              size="small"
              value={props.agentTheme.titleColor}
              onChange={(e) => {
                setTitleColor(e.target.value);
                props.updateTitleColor(e.target.value);
              }}
              inputProps={{ maxLength: 7 }}
              sx={{
                width: "150px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                },
                "& .MuiInputBase-input": {
                  padding: "0 16px",
                  height: "100%",
                  boxSizing: "border-box",
                  borderRadius: "8px",
                },
              }}
            />
            <Box
              sx={{
                width: 44,
                height: 44,
                position: "absolute",
                right: "7px",
                top: "3px",
              }}
            >
              <input
                type="color"
                value={props.agentTheme.titleColor}
                onChange={(e) => {
                  setTitleColor(e.target.value);
                  props.updateTitleColor(e.target.value);
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  background: "#fff",
                  cursor: "pointer",
                  borderRadius: "8px",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  outline: "none",
                  border: "none",
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid #E3E3E3",
            borderRadius: "8px",
            p: 2,
            background: variables.while,
          }}
        >
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Outgoing Chat Text Color
            </Typography>
            <Typography variant="body2">
              You can change the text color of outgoing messages.
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "center", position: "relative" }}
          >
            <TextField
              size="small"
              value={props.agentTheme.outgoingChatTextColor}
              onChange={(e) => {
                setOutgoingTextColor(e.target.value);
                props.updateOutgoingTextColor(e.target.value);
              }}
              inputProps={{ maxLength: 7 }}
              sx={{
                width: "150px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                },
                "& .MuiInputBase-input": {
                  padding: "0 16px",
                  height: "100%",
                  boxSizing: "border-box",
                  borderRadius: "8px",
                },
              }}
            />
            <Box
              sx={{
                width: 44,
                height: 44,
                position: "absolute",
                right: "7px",
                top: "3px",
              }}
            >
              <input
                type="color"
                value={props.agentTheme.outgoingChatTextColor}
                onChange={(e) => {
                  setOutgoingTextColor(e.target.value);
                  props.updateOutgoingTextColor(e.target.value);
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  background: "#fff",
                  cursor: "pointer",
                  borderRadius: "8px",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  outline: "none",
                  border: "none",
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid #E3E3E3",
            borderRadius: "8px",
            p: 2,
            background: variables.while,
          }}
        >
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Incoming Chat Text Color
            </Typography>
            <Typography variant="body2">
              You can change the text color of incoming messages.
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "center", position: "relative" }}
          >
            <TextField
              size="small"
              value={props.agentTheme.incomingChatTextColor}
              onChange={(e) => {
                setIncomingTextColor(e.target.value);
                props.updateIncomingTextColor(e.target.value);
              }}
              inputProps={{ maxLength: 7 }}
              sx={{
                width: "150px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                },
                "& .MuiInputBase-input": {
                  padding: "0 16px",
                  height: "100%",
                  boxSizing: "border-box",
                  borderRadius: "8px",
                },
              }}
            />
            <Box
              sx={{
                width: 44,
                height: 44,
                position: "absolute",
                right: "7px",
                top: "3px",
              }}
            >
              <input
                type="color"
                value={props.agentTheme.incomingChatTextColor}
                onChange={(e) => {
                  setIncomingTextColor(e.target.value);
                  props.updateIncomingTextColor(e.target.value);
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  background: "#fff",
                  cursor: "pointer",
                  borderRadius: "8px",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  outline: "none",
                  border: "none",
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid #E3E3E3",
            borderRadius: "8px",
            p: 2,
            background: variables.while,
          }}
        >
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Chat bubble button color
            </Typography>
            <Typography variant="body2">
              You can change the color of the chat bubble button.
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "center", position: "relative" }}
          >
            <TextField
              size="small"
              value={props.agentTheme.chatBubbleButtonColor}
              onChange={(e) => {
                setBubbleColor(e.target.value);
                props.updateBubbleColor(e.target.value);
              }}
              inputProps={{ maxLength: 7 }}
              sx={{
                width: "150px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                },
                "& .MuiInputBase-input": {
                  padding: "0 16px",
                  height: "100%",
                  boxSizing: "border-box",
                  borderRadius: "8px",
                },
              }}
            />
            <Box
              sx={{
                width: 44,
                height: 44,
                position: "absolute",
                right: "7px",
                top: "3px",
              }}
            >
              <input
                type="color"
                value={props.agentTheme.chatBubbleButtonColor}
                onChange={(e) => {
                  // console.log(e.target.value);
                  setBubbleColor(e.target.value);
                  props.updateBubbleColor(e.target.value);
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  background: "#fff",
                  cursor: "pointer",
                  borderRadius: "8px",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  outline: "none",
                  border: "none",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatInterfaceSettings;
